import { useFormik } from "formik"
import React, { useEffect, useState } from "react"
import { Form, InputGroup, OverlayTrigger, Tooltip } from "react-bootstrap"
import { Link } from "react-router-dom"
import * as yup from "yup"
import axios from "axios"
import { enqueueSnackbar } from "notistack"
import InfoIcon from "@mui/icons-material/Info"
import { FaEye, FaEyeSlash } from "react-icons/fa6"
import { useLocalization } from "../../providers/LocalizationProvider"
import { base_headers } from "../DynamicAssets/constant"
import { BASE_URL } from "../Alerts/Alerts"
import Logo from "../../assets/images/envsg-web-logo.png"
import useLoginBranding from "../../hooks/useLoginBranding"

const ForgotChangePassword = () => {
    const [loading, setLoading] = useState(false)
    const [regex, setRegex] = useState("")
    const [regexMessage, setRegexMessage] = useState("")
    const [verificationInfo, setVerificationInfo] = useState()
    const [pageLoadingState, setPageLoadingState] = useState(false)

    const [showPasswords, setShowPasswords] = useState({
        new: false,
        confirm: false,
    })

    const { voiceiiTags } = useLocalization()
    const { branding } = useLoginBranding()

    const brandedLogo = branding?.logo ? `${BASE_URL}${branding.logo}` : Logo

    const [forgotChangePassword, setForgotChangePassword] = useState({
        new_password: "",
        confirm_new_password: "",
    })

    const [app_key] = useState(BASE_URL.includes("wavsmart") ? "wavsmart" : "efleety")

    const togglePassword = (field) => {
        setShowPasswords((prev) => ({
            ...prev,
            [field]: !prev[field],
        }))
    }

    const changePasswordSchema = yup.object().shape({
        new_password: yup
            .string()
            .matches(
                regex,
                "Your password must be atleast 12 characters long and include one lowercase letter, one uppercase letter, one number and one special character",
            ),
        confirm_new_password: yup
            .string()
            .oneOf([yup.ref("new_password"), null], "Passwords must match"),
    })

    const { values, errors, handleBlur, handleChange, handleSubmit } = useFormik({
        initialValues: {
            new_password: "",
            confirm_new_password: "",
        },
        validationSchema: changePasswordSchema,
        onSubmit: async (values) => {
            const updatedVerificationDetails = {
                ...values,
                ...verificationInfo,
                otp_token: verificationInfo.otpToken,
            }

            try {
                setPageLoadingState(true)

                const response = await axios({
                    method: "POST",
                    url: `${BASE_URL}api/v1/forgot-password`,
                    data: updatedVerificationDetails,
                    headers: base_headers,
                })

                if (response?.status) {
                    enqueueSnackbar(response.data.message, {
                        variant: "success",
                        anchorOrigin: {
                            horizontal: "right",
                            vertical: "bottom",
                        },
                    })

                    setPageLoadingState(false)

                    localStorage.clear()

                    setTimeout(() => {
                        window.location.href = "/"
                    }, 2000)
                } else {
                    enqueueSnackbar("Failed to send password reset link", {
                        variant: "error",
                        anchorOrigin: {
                            horizontal: "right",
                            vertical: "bottom",
                        },
                    })

                    setPageLoadingState(false)
                }
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message, {
                    variant: "error",
                    anchorOrigin: {
                        horizontal: "right",
                        vertical: "bottom",
                    },
                })

                setPageLoadingState(false)
            }
        },
    })

    useEffect(() => {
        setRegex("^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{11,}$")

        setRegexMessage(
            "Your password must be atleast 12 characters long and include one lowercase letter, one uppercase letter, one number and one special character",
        )

        setVerificationInfo(JSON.parse(localStorage.getItem("verificationDetails")))
    }, [])

    const handleChangeWrapper = (event) => {
        const { name, value } = event.target

        setForgotChangePassword((prevState) => ({
            ...prevState,
            [name]: value,
        }))

        handleChange(event)
    }

    return (
        <div>
            <div className="login-img">
                <div className="page page-white">
                    <div className="col-login-img mx-auto" style={{ marginTop: "22vh" }}>
                        <div className="text-center">
                            <Link to="/">
                                <img
                                    style={{
                                        transition: "filter 0.3s ease",
                                    }}
                                    src={brandedLogo}
                                    className="header-brand-img desktop-logo"
                                    alt="logo1"
                                    onMouseEnter={(e) => (e.target.style.filter = "invert(50%)")}
                                    onMouseLeave={(e) => (e.target.style.filter = "invert(0%)")}
                                />
                            </Link>
                        </div>
                    </div>

                    <div className="login-wrapper">
                        <form className="login100-form validate-form" onSubmit={handleSubmit}>
                            <span className="login100-form-title pb-5">
                                {voiceiiTags("Change Password")}
                            </span>

                            <div>
                                <InputGroup
                                    className="wrap-input100 validate-input"
                                    id="Password-toggle"
                                >
                                    <Form.Control
                                        className="input100 ms-0 p-2"
                                        type={showPasswords.new ? "text" : "password"}
                                        name="new_password"
                                        placeholder={voiceiiTags("New Password")}
                                        value={values.new_password}
                                        onChange={handleChangeWrapper}
                                        onBlur={handleBlur}
                                        required
                                    />

                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip style={{ zIndex: "9999" }}>
                                                {voiceiiTags("password_tooltip") || regexMessage}
                                            </Tooltip>
                                        }
                                    >
                                        <InputGroup.Text>
                                            <InfoIcon
                                                style={{
                                                    color: "black",
                                                    width: "20px",
                                                    height: "20px",
                                                }}
                                            />
                                        </InputGroup.Text>
                                    </OverlayTrigger>

                                    <InputGroup.Text
                                        onClick={() => togglePassword("new")}
                                        style={{ cursor: "pointer" }}
                                    >
                                        {showPasswords.new ? (
                                            <FaEyeSlash
                                                style={{
                                                    zIndex: "9999",
                                                    color: "black",
                                                    width: "20px",
                                                    height: "20px",
                                                }}
                                                size={18}
                                            />
                                        ) : (
                                            <FaEye
                                                style={{
                                                    zIndex: "9999",
                                                    color: "black",
                                                    width: "20px",
                                                    height: "20px",
                                                }}
                                                size={18}
                                            />
                                        )}
                                    </InputGroup.Text>
                                </InputGroup>

                                {errors.new_password && (
                                    <div className="text-danger" style={{ marginBottom: "10px" }}>
                                        {voiceiiTags(
                                            '"New Password" Please enter a valid password',
                                        )}
                                    </div>
                                )}

                                {/* Continue in Part 2 */}
                                <InputGroup
                                    className="wrap-input100 validate-input"
                                    id="Password-toggle"
                                >
                                    <Form.Control
                                        className="input100 ms-0 p-2"
                                        type={showPasswords.confirm ? "text" : "password"}
                                        name="confirm_new_password"
                                        placeholder={voiceiiTags("Confirm Password")}
                                        value={values.confirm_new_password}
                                        onChange={handleChangeWrapper}
                                        onBlur={handleBlur}
                                        required
                                    />

                                    <InputGroup.Text
                                        style={{ cursor: "pointer" }}
                                        onClick={() => togglePassword("confirm")}
                                    >
                                        {showPasswords.confirm ? (
                                            <FaEyeSlash
                                                style={{
                                                    color: "black",
                                                    width: "20px",
                                                    height: "20px",
                                                }}
                                                size={18}
                                            />
                                        ) : (
                                            <FaEye
                                                style={{
                                                    color: "black",
                                                    width: "20px",
                                                    height: "20px",
                                                }}
                                                size={18}
                                            />
                                        )}
                                    </InputGroup.Text>
                                </InputGroup>

                                {errors.confirm_new_password && (
                                    <span className="text-danger">
                                        {errors.confirm_new_password}
                                    </span>
                                )}

                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        gap: "10px",
                                    }}
                                >
                                    <div className="wrapper-login100-form-btn">
                                        <button
                                            type="submit"
                                            className="login100-form-btn btn-secondary"
                                            disabled={
                                                loading ||
                                                !(
                                                    values.new_password.trim() !== "" &&
                                                    values.confirm_new_password.trim() !== "" &&
                                                    values.confirm_new_password ===
                                                        values.new_password &&
                                                    values.new_password.match(regex)
                                                )
                                            }
                                            style={{
                                                width: "170px",
                                                border: "none",
                                                opacity: loading ? "0.6" : "1",
                                                backgroundColor:
                                                    loading ||
                                                    !(
                                                        values.new_password.trim() !== "" &&
                                                        values.confirm_new_password.trim() !== "" &&
                                                        values.confirm_new_password ===
                                                            values.new_password &&
                                                        values.new_password.match(regex)
                                                    )
                                                        ? "#d3d3d3"
                                                        : app_key === "wavsmart"
                                                          ? "#4B286D"
                                                          : "",
                                            }}
                                        >
                                            {voiceiiTags("Change Password")}

                                            {pageLoadingState && (
                                                <span
                                                    role="status"
                                                    aria-hidden="true"
                                                    className="spinner-border spinner-border-sm ms-2"
                                                />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}

ForgotChangePassword.propTypes = {}

ForgotChangePassword.defaultProps = {}

export default ForgotChangePassword
